import { COLOR_PRIMARY } from "#constants/Styles"
import AssignmentIcon from "@mui/icons-material/Assignment"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import PersonIcon from "@mui/icons-material/Person"
import { Box, Button, Chip, Divider, Paper, Stack, Typography } from "@mui/material"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import { useNavigate } from "react-router-dom"
import { MyEvaluationsCardButtons } from "./MyEvaluationsCardButtons"
import { useColaboradorId } from "app/userSlice"
import { UseHasPermission } from "#hooks/useHasPermission"
import PERMISOS from "#constants/perfiles"

export function MyEvaluationsCard({ evaluation, jefe_directo, onClick }) {
	return (
		<Card
			onClick={onClick}
			elevation={2}
			sx={{
				minWidth: "90vw",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				cursor: onClick ? "pointer" : "default",
				borderRadius: 3,
				overflow: "hidden",
				transition: "all 0.3s ease-in-out",
				"&:hover": {
					boxShadow: onClick ? 8 : 2,
					transform: onClick ? "translateY(-4px)" : "none",
				},
				border: `3px solid ${COLOR_PRIMARY}20`,
			}}
		>
			{/* Content */}
			<CardContent sx={{ flexGrow: 1, p: 0, "&:last-child": { pb: 0 } }}>
				<Box sx={{ p: 0, display: "flex", flexDirection: "row", gap: 2 }}>
					<Box
						sx={{
							background: COLOR_PRIMARY,
							p: 2.5,
							color: "white",
							minWidth: "40vw",
						}}
					>
						<Stack
							spacing={2}
							direction={{ xs: "column", md: "column" }}
							alignItems="center"
							justifyContent="space-between"
						>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
								<CalendarTodayIcon sx={{ fontSize: 20 }} />
								<Box>
									<Typography variant="caption" sx={{ opacity: 0.9, display: "block" }}>
										Fecha de Evaluación
									</Typography>
									<Typography variant="body1" sx={{ fontWeight: 600 }}>
										{evaluation.created_at}
									</Typography>
								</Box>
							</Box>

							<Chip
								icon={<AssignmentIcon sx={{ color: "white !important" }} />}
								label={evaluation.status_name}
								size="medium"
								sx={{
									bgColor: "rgba(255, 255, 255, 0.25)",
									color: "white",
									fontWeight: 600,
									backdropFilter: "blur(10px)",
									border: "1px solid rgba(255, 255, 255, 0.3)",
								}}
							/>
							<MyEvaluationsCardButtons
								evaluation={evaluation}
								jefe_directo={jefe_directo}
							/>
						</Stack>
					</Box>
					<Stack
						width={"100%"}
						p={1}
						spacing={2}
						direction={{ xs: "column-reverse", lg: "row-reverse" }}
						justifyContent="space-around"
					>
						<MyEvaluationsCardEvaluation
							evaluations={evaluation.evaluations_evaluators}
							status={evaluation.status}
						/>
					</Stack>
				</Box>
			</CardContent>
		</Card>
	)
}

const MyEvaluationsCardEvaluation = ({ evaluations, onClick, status }) => {
	let navigate = useNavigate()
	const colaboradorId = useColaboradorId()
	const isRh = UseHasPermission(PERMISOS.RH)
	if (!evaluations || evaluations.length === 0) return null

	const showButton = (evaluator_type, evaluator) => {
		if (!isRh && colaboradorId !== evaluator) return false
		if (status === 1 && evaluator_type === 2) return true
		if (status === 2 && evaluator_type === 1) return true
		return false
	}

	return evaluations.map((evaluation, index) => (
		<Paper
			key={evaluation?.id}
			onClick={onClick}
			elevation={1}
			sx={{
				width: { xs: "100%", md: "250px" },
				minHeight: "150px",
				display: "flex",
				flexDirection: "column",
				borderRadius: 2.5,
				overflow: "hidden",
				transition: "all 0.3s ease",
				border: "1px solid",
				borderColor: "divider",
			}}
		>
			{/* Header del evaluador */}
			<Box
				sx={{
					background: COLOR_PRIMARY,
					p: 0.2,
					color: "white",
					textAlign: "center",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 1,
				}}
			>
				<PersonIcon color="white" />
				<Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
					{evaluation.evaluator_type_name}
				</Typography>
			</Box>

			{/* Content */}
			<Box
				sx={{
					p: 0.5,
					flexGrow: 1,
					display: "flex",
					flexDirection: "column",
					gap: 2,
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				{showButton(evaluation.evaluator_type, evaluation.evaluator) && (
					<>
						<Divider />
						<Button
							fullWidth
							size="medium"
							onClick={() => navigate(`../performance_evaluation/form/${evaluation.id}`)}
							variant="text"
							startIcon={<AssignmentIcon />}
							sx={{
								py: 1.2,
								fontWeight: 600,
								borderRadius: 2,
								"&:hover": {
									transform: "scale(1.02)",
								},
							}}
						>
							Realizar Evaluación
						</Button>
					</>
				)}
				<NoScore
					score={evaluation.score_competencies}
					button={showButton(evaluation.evaluator_type)}
				/>
				<MyEvaluationScore score={evaluation.score_competencies} />
			</Box>
		</Paper>
	))
}

const NoScore = ({ score, button }) => {
	if (score || button) return null

	return (
		<Box>
			<Typography>No evaluado</Typography>
			<Divider />
		</Box>
	)
}

const MyEvaluationScore = ({ score }) => {
	if (!score) return null
	const getScoreColor = () => {
		if (score >= 4.5) return "success"
		if (score >= 3.5) return "primary"
		if (score >= 2.5) return "warning"
		return "error"
	}
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				flexDirection: "column",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					gap: 1,
					p: 0.5,
				}}
			>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{
						fontWeight: 600,
						textTransform: "uppercase",
						letterSpacing: 0.5,
						mb: 0.5,
						display: "block",
					}}
				>
					promedio general
				</Typography>

				{/* Score con diseño circular */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 0.5,
						flexDirection: "column",
					}}
				>
					<Box
						sx={{
							width: 70,
							height: 70,
							borderRadius: "50%",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							border: "4px solid",
							borderColor: `${getScoreColor()}.main`,
							bgColor: `${getScoreColor()}.light`,
						}}
					>
						<Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
							{score}
						</Typography>
						<Typography variant="caption" sx={{ fontSize: "0.65rem", opacity: 0.8 }}>
							de 5
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	)
}
